// frontend/src/pages/dashboard/TaskComponents/TaskCard.js

import React, { memo } from "react";
import {
  Paper,
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
  Badge,
  Collapse,
  Button,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Timeline as TimelineIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  Error as ErrorIcon,
  AttachFile as AttachFileIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import moment from "moment-timezone";
import { convertToCompanyTime } from "../../../utils/taskHelpers";
import { getAvatarUrl } from "../../../utils/imageHelpers";
import TaskCommentSection from "./TaskCommentSection";
import TaskHistoryLog from "./TaskHistoryLog";

const TaskCard = memo(
  ({
    task,
    index,
    handlers,
    authUser,
    hasPermission,
    theme,
    company,
    newCommentText,
    setNewCommentText,
    commentFiles,
    setCommentFiles,
    taskComments,
    commentLoading,
    handleDownload,
    handleImagePreview,
    expandedTaskId,
    expandedSection, 
    sprints,
  }) => {
    const dueDateLocal = task.due_date
      ? moment.utc(task.due_date).tz(company.timezone)
      : null;
    const isOverdue =
      dueDateLocal &&
      moment() > dueDateLocal &&
      !["completed", "done"].includes(task.statusObj?.key);
    const isExpanded = expandedTaskId === task.id;

    return (
      <Paper
        sx={{
          borderRadius: 2,
          boxShadow: 2,
          p: 2,
          transition: "box-shadow 0.3s ease",
          "&:hover": { boxShadow: 4 },
          position: "relative",
          overflow: "visible",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {task.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ maxWidth: 200 }}
            >
              {task.description}
            </Typography>
          </Box>
          <Chip
            label={String(index + 1).padStart(2, "0")}
            size="small"
            sx={{ alignSelf: "flex-start" }}
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <Button
                  size="small"
                  onClick={() => handlers.onViewProject(task.project_id)}
                >
                  {task.project?.name || "N/A"}
                </Button>
              </Typography>
            </Box>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TimelineIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {task.sprint_id ? (
                  <Chip
                    label={
                      sprints.find(
                        (s) => String(s.id) === String(task.sprint_id)
                      )?.name || "Unknown"
                    }
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ) : (
                  "No Sprint"
                )}
              </Typography>
            </Box>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {task.due_date
                  ? convertToCompanyTime(task.due_date, company.timezone)
                  : "N/A"}
              </Typography>
            </Box>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTimeIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {task.tracked_time?.formatted || "00h 00m"}
              </Typography>
              {task.tracked_time?.status === "Running" && (
                <Chip
                  label="● LIVE"
                  size="small"
                  color="success"
                  sx={{ fontSize: "0.65rem", height: 20 }}
                />
              )}
              {task.tracked_time?.status === "Ended" && (
                <Chip
                  label="⏹️ ENDED"
                  size="small"
                  color="default"
                  sx={{ fontSize: "0.65rem", height: 20 }}
                />
              )}
              {task.tracked_time?.status === "Paused" && (
                <Chip
                  label="⏸️ PAUSED"
                  size="small"
                  color="warning"
                  sx={{ fontSize: "0.65rem", height: 20 }}
                />
              )}
            </Box>
          </Grid>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssignmentIcon fontSize="small" color="action" />
              <Chip
                label={
                  task.priority.charAt(0).toUpperCase() + task.priority.slice(1)
                }
                color={
                  task.priority === "high" || task.priority === "urgent"
                    ? "error"
                    : task.priority === "low"
                    ? "success"
                    : "warning"
                }
                size="small"
              />
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              gutterBottom
            >
              ASSIGNED TO
            </Typography>
            {task.assignedUsers?.length > 0 ? (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {task.assignedUsers.map((user) => (
                  <Tooltip key={user.id} title={user.name}>
                    <Avatar
                      sx={{ width: 24, height: 24, fontSize: 12 }}
                      src={getAvatarUrl(user.avatar)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = null;
                      }}
                    >
                      {!user.avatar ? user.name.charAt(0) : null}
                    </Avatar>
                  </Tooltip>
                ))}
              </Box>
            ) : (
              "Unassigned"
            )}
          </Box>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              gutterBottom
            >
              STATUS
            </Typography>
            <Chip
              label={(task.statusObj?.name || "Unknown").toUpperCase()}
              sx={{
                backgroundColor: task.statusObj?.color || "#9CA3AF",
                color: "#fff",
              }}
              size="small"
            />
          </Box>
        </Box>

        {isOverdue && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <ErrorIcon fontSize="small" color="error" />
            <Typography variant="body2" color="error">
              This task is overdue
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {task.attachments && task.attachments.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  color: "primary.main",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.onViewAttachments(task.attachments);
                }}
              >
                <AttachFileIcon fontSize="small" />
                <Typography variant="caption" component="span">
                  {task.attachments.length} file(s)
                </Typography>
              </Box>
            )}
            <Badge
              badgeContent={task.comment_count || 0}
              color="primary"
              max={99}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.onToggleComments(task.id);
                }}
              >
                <ChatBubbleOutlineIcon />
              </IconButton>
            </Badge>
          </Box>
          <Box>
            {(() => {
              const isAssigned = task.assignedUsers.some(
                (u) => u.id === authUser?.id
              );
              const isCreator = task.created_by === authUser?.id;
              const canAct =
                hasPermission("task.view.all") || isAssigned || isCreator;
              if (!canAct) return null;
              return (
                <>
                  <Tooltip title={`${task.history_count || 0} history logs`} arrow>
                    <Badge
                      badgeContent={task.history_count || 0}
                      color="secondary"
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlers.onToggleHistory(task.id);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Badge>
                  </Tooltip>
                  {(hasPermission("task.update") ||
                    hasPermission("task.manage.team") ||
                    isAssigned) && (
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handlers.onEdit(task)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasPermission("task.delete") && (
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => handlers.onDelete(task.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasPermission("task.view.own") && (
                    <Tooltip title="View Details">
                      <IconButton onClick={() => handlers.onViewDetails(task)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              );
            })()}
          </Box>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            
            {/* Tab Switcher */}
            <Box sx={{ 
              display: 'flex', 
              mb: 2,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.onToggleComments?.(task.id);
                }}
                sx={{
                  borderBottom: expandedSection === 'comments' ? `2px solid ${theme.palette.primary.main}` : 'none',
                  borderRadius: 0,
                  color: expandedSection === 'comments' ? 'primary.main' : 'text.secondary',
                  fontWeight: expandedSection === 'comments' ? 600 : 400,
                  px: 2,
                  py: 1,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  minWidth: 'auto'
                }}
              >
                <ChatBubbleOutlineIcon fontSize="small" /> Comments ({task.comment_count || 0})
              </Button>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.onToggleHistory?.(task.id);
                }}
                sx={{
                  borderBottom: expandedSection === 'history' ? `2px solid ${theme.palette.secondary.main}` : 'none',
                  borderRadius: 0,
                  color: expandedSection === 'history' ? 'secondary.main' : 'text.secondary',
                  fontWeight: expandedSection === 'history' ? 600 : 400,
                  px: 2,
                  py: 1,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  minWidth: 'auto'
                }}
              >
                <HistoryIcon fontSize="small" /> Activity Log ({task.history_count || 0})
              </Button>
            </Box>

            {/* Conditional Content */}
            {expandedSection === 'comments' && (
              <TaskCommentSection
                taskId={task.id}
                comments={taskComments[task.id] || []}
                loading={commentLoading[task.id]}
                onPostComment={handlers.onPostComment}
                onDeleteComment={handlers.onDeleteComment}
                users={task.assignedUsers || []}
                authUser={authUser}
                newCommentText={newCommentText}
                setNewCommentText={setNewCommentText}
                commentFiles={commentFiles}
                setCommentFiles={setCommentFiles}
                theme={theme}
                hasPermission={hasPermission}
                handleDownload={handleDownload}
                handleImagePreview={handleImagePreview}
              />
            )}

            {expandedSection === 'history' && (
              <TaskHistoryLog
                taskId={task.id}
                company={company}
                theme={theme}
              />
            )}

            {/* Fallback: Show comments by default if expandedSection is null */}
            {!expandedSection && (
              <TaskCommentSection
                taskId={task.id}
                comments={taskComments[task.id] || []}
                loading={commentLoading[task.id]}
                onPostComment={handlers.onPostComment}
                onDeleteComment={handlers.onDeleteComment}
                users={task.assignedUsers || []}
                authUser={authUser}
                newCommentText={newCommentText}
                setNewCommentText={setNewCommentText}
                commentFiles={commentFiles}
                setCommentFiles={setCommentFiles}
                theme={theme}
                hasPermission={hasPermission}
                handleDownload={handleDownload}
                handleImagePreview={handleImagePreview}
              />
            )}
          </Box>
        </Collapse>
      </Paper>
    );
  }
);

export default TaskCard;